import React from 'react';
import type { WrappedComponentProps } from 'react-intl-next';
import { defineMessages, injectIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { AUTOMATION_SMART_BUTTON_PREVIEW_RULE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';

import { getTextColorByComponentType, getBackgroundColor } from '../../utils/colorUtils.ts';
import { ComponentType } from '../../edit/SmartButtonConfigPanel/templates/templateCardConfigs.tsx';
import type { AutomationRuleSummary } from '../../__types__/AutomationRuleSummaryQuery.ts';

import { ErrorRulePreview } from './ErrorRulePreview.tsx';
import { RuleActionsPreview } from './RuleActionsPreview.tsx';
import { RulePreviewSkeleton } from './RulePreviewSkeleton.tsx';

type RulePreviewProps = {
	automationRuleSummaryData?: AutomationRuleSummary;
	automationRuleSummaryLoading: boolean;
	automationRuleSummaryError?: ApolloErrorInterop | Error;
};

export const i18n = defineMessages({
	whenButtonClicked: {
		id: 'automation-extensions.smart-button.preview.when-clicked',
		defaultMessage: 'When Smart button is clicked',
		description:
			"The 'When Smart button is clicked' in 'When Smart button is clicked then' rule preview tooltip",
	},
	then: {
		id: 'automation-extensions.smart-button.preview.then',
		defaultMessage: 'Then',
		description: "The 'then' in 'When Smart button is clicked then' rule preview tooltip",
	},
});

const RulePreviewComponent = ({
	automationRuleSummaryData,
	automationRuleSummaryLoading,
	automationRuleSummaryError,
	intl,
}: RulePreviewProps & WrappedComponentProps) => {
	const automationRuleSummary =
		automationRuleSummaryData?.experimentalAutomationRuleSummary ?? undefined;

	if (automationRuleSummaryError) {
		return <ErrorRulePreview />;
	}
	if (automationRuleSummaryLoading || !automationRuleSummary) {
		return <RulePreviewSkeleton />;
	}

	const { componentSummaries } = automationRuleSummary;

	const actionSummaries = componentSummaries.filter(
		(summary) => summary.component === ComponentType.ACTION,
	);

	return (
		<Stack space="space.100">
			<Box
				backgroundColor={getBackgroundColor(ComponentType.TRIGGER)}
				paddingBlock="space.025"
				paddingInline="space.050"
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
				xcss={xcss({
					borderRadius: 'radius.small',
					width: '100%',
				})}
			>
				<Text
					align="center"
					color={getTextColorByComponentType(ComponentType.TRIGGER)}
					size="small"
					weight="semibold"
				>
					{intl.formatMessage(i18n.whenButtonClicked)}
				</Text>
			</Box>
			<Stack space="space.100">
				<Box
					backgroundColor={getBackgroundColor(ComponentType.ACTION)}
					paddingBlock="space.025"
					paddingInline="space.050"
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
					xcss={xcss({
						borderRadius: 'radius.small',
						width: 'fit-content',
					})}
				>
					<Text
						color={getTextColorByComponentType(ComponentType.ACTION)}
						size="small"
						weight="semibold"
					>
						{intl.formatMessage(i18n.then)}
					</Text>
				</Box>
				<RuleActionsPreview actionSummaries={actionSummaries} />
			</Stack>
			<ExperienceSuccess name={AUTOMATION_SMART_BUTTON_PREVIEW_RULE} />
		</Stack>
	);
};

export const RulePreview = injectIntl(RulePreviewComponent);
